import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Ban, Briefcase, Flag, Handshake, Image as ImageIcon, Mail, MessageCircle, Pencil, Phone, ThumbsUp, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdate, fphone } from "@/lib/format";
import { useAuth, can } from "@/context/AuthContext";
import { Avatar, EmptyState, Loader } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MediaGrid, PostCard } from "@/components/member/Feed";
import { Cover } from "@/components/member/ProfileMedia";

const PTABS = [["apropos", "À propos"], ["publications", "Publications"], ["medias", "Médias"]];

function AuthorPosts({ id, media }) {
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    setPosts(null);
    api.get("/feed", { params: { author_id: id, media } }).then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  }, [id, media]);
  if (posts === null) return <Loader />;
  if (!posts.length) return <EmptyState icon={media ? ImageIcon : MessageCircle} title={media ? "Aucun média partagé" : "Aucune publication"} text="Ce qui sera partagé dans le fil apparaîtra ici." />;
  if (media) return <div className="grid sm:grid-cols-2 gap-3" data-testid="profile-media-grid">{posts.map((p) => <div key={p.id} className="mr-card p-2"><MediaGrid media={p.media} /></div>)}</div>;
  return <ul className="space-y-4" data-testid="profile-posts">{posts.map((p) => <PostCard key={p.id} post={p} />)}</ul>;
}

const Info = ({ label, children }) => (
  <div>
    <p className="text-xs uppercase tracking-wider text-mr-muted mb-1">{label}</p>
    <p className="text-[15px] text-mr-text2">{children || "—"}</p>
  </div>
);

function PickMember({ title, onPick, onClose, contactsOnly }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    if (contactsOnly) {
      api.get("/me/overview").then(({ data }) => setItems(data.contacts.filter((c) => c.status === "accepte")));
      return;
    }
    if (q.length < 2) return;
    api.get("/search", { params: { q } }).then(({ data }) => setItems(data.members));
  }, [q, contactsOnly]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="pick-member-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {chosen ? (
          <div className="space-y-3">
            <p className="text-sm">Avec <strong>{chosen.name}</strong></p>
            <textarea data-testid="pick-member-message" className="mr-input min-h-[80px]" placeholder="Message (facultatif)" value={msg} onChange={(e) => setMsg(e.target.value)} />
            <div className="flex gap-2"><button data-testid="pick-member-confirm" className="mr-btn-primary" onClick={() => onPick(chosen, msg)}>Confirmer</button>
              <button className="mr-btn-ghost" onClick={() => setChosen(null)}>Changer</button></div>
          </div>
        ) : (
          <>
            {!contactsOnly && <input data-testid="pick-member-input" className="mr-input" placeholder="Rechercher un membre" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />}
            <ul className="space-y-1 max-h-72 overflow-y-auto">{items.map((m) => (
              <li key={m.id}><button data-testid="pick-member-option" className="w-full text-left p-3 hover:bg-mr-raised rounded-[3px] text-sm" onClick={() => setChosen(m)}>{m.name} <span className="text-mr-muted">· {m.company}</span></button></li>))}</ul>
          </>
        )}
        {contactsOnly && items.length === 0 && <p className="text-sm text-mr-text2">Vous n'avez pas encore de contact à qui le demander.</p>}
      </DialogContent>
    </Dialog>
  );
}

export default function MemberDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { me } = useAuth();
  const [m, setM] = useState(null);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);
  const [tab, setTab] = useState("apropos");
  const load = () => api.get(`/members/${id}`).then(({ data }) => setM(data)).catch((e) => setError(errMsg(e)));
  useEffect(() => {
    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn, ok) => {
    try {
      await fn();
      if (ok) toast.success(ok);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const message = () => act(async () => { const { data } = await api.post("/conversations", { member_id: id }); nav(`/app/messages/${data.id}`); });
  const contact = () => act(() => (m.contact_incoming ? api.post(`/members/${id}/contact/respond`, { accept: true }) : api.post(`/members/${id}/contact`)), "Demande envoyée");
  const recommend = () => { const text = window.prompt("Votre recommandation"); if (text) act(() => api.post(`/members/${id}/recommend`, { text }), "Recommandation publiée"); };
  const report = () => { const reason = window.prompt("Motif du signalement"); if (reason) act(() => api.post(`/members/${id}/report`, { reason }), "Signalement transmis"); };
  const block = () => act(() => (m.blocked ? api.delete(`/members/${id}/block`) : window.confirm("Bloquer ce membre ?") && api.post(`/members/${id}/block`)), m.blocked ? "Membre débloqué" : "Membre bloqué");
  const inviteOp = async () => {
    const { data } = await api.get("/me/overview");
    const ops = data.operations.filter((o) => o.role === "adherent");
    if (!ops.length) return toast.error("Vous devez participer à une opération pour y inviter un membre.");
    act(() => api.post(`/members/${id}/invite-operation`, { operation_id: ops[0].id }), `Invitation envoyée pour « ${ops[0].title} »`);
  };

  if (error && !m) return <EmptyState title="Membre introuvable" text={error} />;
  if (!m) return <Loader />;
  const c = m.contacts;
  const self = m.id === me.user.id;
  const canAct = can(me, "message:send");
  return (
    <div className="mr-fade-up space-y-6 max-w-4xl mx-auto" data-testid="member-detail">
      <button data-testid="member-detail-back-button" className="mr-btn-ghost -ml-3" onClick={() => nav(-1)}><ArrowLeft size={16} /> Retour</button>
      <div className="mr-card p-0 overflow-hidden" data-testid="profile-header">
        <Cover fileId={m.cover_file_id} className="h-32 sm:h-48" />
        <div className="px-5 sm:px-8 pb-6">
          <div className="flex flex-wrap items-end justify-between gap-4 -mt-12">
            <div className="relative z-10 rounded-full ring-4 ring-mr-surface"><Avatar name={m.name} fileId={m.photo_file_id} size={96} /></div>
            {self && <Link to="/app/moi?tab=profil" data-testid="profile-edit-link" className="mr-btn-secondary"><Pencil size={15} /> Modifier mon profil</Link>}
          </div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mt-4" data-testid="member-detail-name">{m.name}</h1>
          {m.headline && <p className="text-mr-text mt-1" data-testid="member-headline">{m.headline}</p>}
          <p className="text-mr-text2 text-sm mt-1">{[m.company, m.city].filter(Boolean).join(" · ")}</p>
          <p className="text-xs text-mr-muted tabular mt-1" data-testid="member-since">Membre depuis {fdate(m.member_since)}{m.sponsor ? ` · parrainé par ${m.sponsor.name}` : ""}</p>
          {m.stats && <div className="flex gap-6 mt-4 text-sm" data-testid="member-stats">{[["posts", "publications"], ["contacts", "contacts"], ["operations", "opérations"]].map(([k, l]) => <span key={k}><span className="font-semibold tabular">{m.stats[k]}</span> <span className="text-mr-muted">{l}</span></span>)}</div>}
          {m.badges.length > 0 && <div className="flex flex-wrap gap-2 mt-4" data-testid="member-badges">{m.badges.map((b) => <span key={b} className="text-xs border border-mr-accent text-mr-accent rounded-full px-2.5 py-1">{b}</span>)}</div>}
        </div>
      </div>
      {!self && canAct && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="member-actions">
          <button data-testid="member-detail-message-button" className="mr-btn-primary" onClick={message}><MessageCircle size={16} /> Message</button>
          <button data-testid="member-contact-button" className="mr-btn-secondary" onClick={contact} disabled={m.contact_status === "accepte" || (m.contact_status === "demande" && !m.contact_incoming)}>
            <UserPlus size={16} /> {m.contact_status === "accepte" ? "Contact" : m.contact_incoming ? "Accepter" : m.contact_status === "demande" ? "Demandé" : "Ajouter"}</button>
          <button data-testid="member-intro-request-button" className="mr-btn-secondary" onClick={() => setDialog("ask")}><Handshake size={16} /> Être présenté</button>
          <button data-testid="member-introduce-button" className="mr-btn-secondary" onClick={() => setDialog("introduce")}><Handshake size={16} /> Présenter</button>
          <button data-testid="member-recommend-button" className="mr-btn-ghost" onClick={recommend} disabled={m.recommended}><ThumbsUp size={16} /> {m.recommended ? "Recommandé" : "Recommander"}</button>
          <button data-testid="member-invite-operation-button" className="mr-btn-ghost" onClick={inviteOp}><Briefcase size={16} /> Inviter à une opération</button>
          <button data-testid="member-report-button" className="mr-btn-ghost" onClick={report}><Flag size={16} /> Signaler</button>
          <button data-testid="member-block-button" className="mr-btn-ghost text-mr-danger" onClick={block}><Ban size={16} /> {m.blocked ? "Débloquer" : "Bloquer"}</button>
        </div>
      )}
      <div className="flex gap-1 border-b border-mr-border" role="tablist">{PTABS.map(([k, l]) => (
        <button key={k} role="tab" aria-selected={tab === k} data-testid={`profile-tab-${k}`} onClick={() => setTab(k)}
          className={`px-4 h-11 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-mr-accent text-mr-text" : "border-transparent text-mr-muted hover:text-mr-text"}`}>{l}</button>))}</div>
      {tab === "publications" && <AuthorPosts id={id} media={false} />}
      {tab === "medias" && <AuthorPosts id={id} media />}
      {tab === "apropos" && <>
      <div className="mr-card grid sm:grid-cols-2 gap-5">
        <Info label="Secteurs">{m.sectors.join(", ")}</Info>
        <Info label="Produits">{[...m.product_categories, ...m.products].join(", ")}</Info>
        <Info label="Pays d'approvisionnement">{m.sourcing_countries.join(", ")}</Info>
        <Info label="Langues">{m.languages.join(", ")}</Info>
        <Info label="J'apporte">{m.brings}</Info>
        <Info label="Je cherche">{m.seeks}</Info>
        <div className="sm:col-span-2"><Info label="Présentation">{m.bio}</Info></div>
      </div>
      <div className="mr-card space-y-3" data-testid="member-detail-reputation">
        <p className="text-xs uppercase tracking-wider text-mr-muted">Opérations et réputation</p>
        <p className="text-sm text-mr-text2" data-testid="member-ratings">{m.ratings.count ? `Note moyenne ${m.ratings.average}/5 (${m.ratings.count} avis)` : "Aucune note reçue pour l'instant."}</p>
        {m.operations.map((o) => <Link key={o.id} to={`/app/operations/${o.id}`} className="block text-sm text-mr-accent" data-testid="member-operation-link">{o.title}</Link>)}
        {m.recommendations.map((r, k) => <p key={k} className="text-sm border-l-2 border-mr-accent pl-3" data-testid="member-recommendation">« {r.text} » <span className="text-mr-muted">— {r.author_name}</span></p>)}
      </div>
      <div className="mr-card space-y-3" data-testid="member-detail-contacts">
        <p className="text-xs uppercase tracking-wider text-mr-muted">Coordonnées</p>
        {Object.keys(c).length === 0 && <p className="text-sm text-mr-text2">Ce membre ne partage pas ses coordonnées. Écrivez-lui par la messagerie.</p>}
        {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-3 text-sm tabular" data-testid="member-contact-phone"><Phone size={16} className="text-mr-muted" />{fphone(c.phone)}</a>}
        {c.whatsapp && <a href={`https://wa.me/${c.whatsapp.replace("+", "")}`} className="flex items-center gap-3 text-sm tabular" data-testid="member-contact-whatsapp"><MessageCircle size={16} className="text-mr-muted" />WhatsApp {fphone(c.whatsapp)}</a>}
        {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-3 text-sm" data-testid="member-contact-email"><Mail size={16} className="text-mr-muted" />{c.email}</a>}
      </div>
      </>}
      {dialog === "ask" && <PickMember contactsOnly title={`Qui peut vous présenter ${m.name} ?`} onClose={() => setDialog(null)}
        onPick={(x, message) => { setDialog(null); act(() => api.post("/introductions/request", { introducer_id: x.id, target_id: id, message }), "Demande de mise en relation envoyée"); }} />}
      {dialog === "introduce" && <PickMember title={`Présenter ${m.name} à…`} onClose={() => setDialog(null)}
        onPick={(x, message) => { setDialog(null); act(() => api.post("/introductions", { member_b_id: id, member_c_id: x.id, message }), "Mise en relation proposée"); }} />}
    </div>
  );
}
